'use client';

import { c as _c } from "react/compiler-runtime";
import { CheckCircleSolid, InfoCircleSolid, Trash, WarningCircleSolid, Xmark } from '@byway/icons';
import { useToast, useToastRegion } from '@react-aria/toast';
import { ToastQueue, type ToastState, useToastQueue } from '@react-stately/toast';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import React, { type AnimationEvent, createContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '~/components/Button';
import styles from './Toast.module.css';
import type { MyToast, ToastProps, ToastRegionProps } from './Toast.types';
const ICONS = {
  success: <CheckCircleSolid size="large" color="var(--by-color-icon-success)" decorative />,
  warning: <WarningCircleSolid size="large" color="var(--by-color-icon-error)" decorative />,
  info: <InfoCircleSolid size="large" color="var(--by-color-icon-informational)" decorative />,
  trash: <Trash size="large" color="var(--by-color-icon-error)" decorative />
};
export const ToastContext = createContext<ToastState<MyToast> | null>(null);
export function Toast(t0) {
  const $ = _c(57);
  let props;
  let state;
  if ($[0] !== t0) {
    ({
      state,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = state;
  } else {
    props = $[1];
    state = $[2];
  }
  const t = useTranslations("aria");
  const ref = useRef(null);
  const {
    toastProps,
    titleProps,
    closeButtonProps
  } = useToast(props, state, ref);
  const content = props.toast.content;
  let t1;
  if ($[3] !== props.toast.animation || $[4] !== props.toast.key || $[5] !== state) {
    t1 = event => {
      if (event.target === event.currentTarget && props.toast.animation === "exiting") {
        state.remove(props.toast.key);
      }
    };
    $[3] = props.toast.animation;
    $[4] = props.toast.key;
    $[5] = state;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const onAnimationEnd = t1;
  let t2;
  if ($[7] !== content.width) {
    t2 = content.width ? {
      width: content.width,
      maxWidth: "100%"
    } : undefined;
    $[7] = content.width;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const widthStyle = t2;
  if (content.render) {
    const t3 = props.toast.animation;
    let t4;
    if ($[9] !== content.shadow) {
      t4 = clsx(styles.toast, {
        [styles.shadow]: content.shadow
      });
      $[9] = content.shadow;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== content || $[12] !== props.toast.key || $[13] !== state) {
      let t6;
      if ($[15] !== props.toast.key || $[16] !== state) {
        t6 = () => state.close(props.toast.key);
        $[15] = props.toast.key;
        $[16] = state;
        $[17] = t6;
      } else {
        t6 = $[17];
      }
      t5 = content.render(t6);
      $[11] = content;
      $[12] = props.toast.key;
      $[13] = state;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[18] !== onAnimationEnd || $[19] !== props.toast.animation || $[20] !== t4 || $[21] !== t5 || $[22] !== toastProps || $[23] !== widthStyle) {
      t6 = <div {...toastProps} ref={ref} data-animation={t3} onAnimationEnd={onAnimationEnd} className={t4} style={widthStyle}>{t5}</div>;
      $[18] = onAnimationEnd;
      $[19] = props.toast.animation;
      $[20] = t4;
      $[21] = t5;
      $[22] = toastProps;
      $[23] = widthStyle;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    return t6;
  }
  const {
    variant,
    title,
    description
  } = content;
  const t3 = props.toast.animation;
  const t4 = variant === "success";
  const t5 = variant === "error";
  const t6 = variant === "warning";
  const t7 = variant === "info";
  const t8 = variant === "trash";
  let t9;
  if ($[25] !== content.shadow || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8) {
    t9 = clsx([styles.toast, {
      [styles.success]: t4,
      [styles.error]: t5,
      [styles.warning]: t6,
      [styles.info]: t7,
      [styles.trash]: t8,
      [styles.shadow]: content.shadow
    }]);
    $[25] = content.shadow;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== variant) {
    t10 = variant !== "error" && <div className={styles.icon}>{ICONS[variant]}</div>;
    $[32] = variant;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== title || $[35] !== titleProps) {
    t11 = <div {...titleProps} className={styles.title}>{title}</div>;
    $[34] = title;
    $[35] = titleProps;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== description) {
    t12 = description && <div className={styles.description}>{description}</div>;
    $[37] = description;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t11 || $[40] !== t12) {
    t13 = <div className={styles.content}>{t11}{t12}</div>;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t) {
    t14 = t("close");
    $[42] = t;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Xmark color="black" size="large" decorative={true} />;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== closeButtonProps || $[46] !== t14) {
    t16 = <Button isTransparent={true} size="small" variant="icon" {...closeButtonProps} ariaLabel={t14}>{t15}</Button>;
    $[45] = closeButtonProps;
    $[46] = t14;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== onAnimationEnd || $[49] !== props.toast.animation || $[50] !== t10 || $[51] !== t13 || $[52] !== t16 || $[53] !== t9 || $[54] !== toastProps || $[55] !== widthStyle) {
    t17 = <div {...toastProps} ref={ref} data-animation={t3} onAnimationEnd={onAnimationEnd} style={widthStyle} className={t9}>{t10}{t13}{t16}</div>;
    $[48] = onAnimationEnd;
    $[49] = props.toast.animation;
    $[50] = t10;
    $[51] = t13;
    $[52] = t16;
    $[53] = t9;
    $[54] = toastProps;
    $[55] = widthStyle;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  return t17;
}
export function ToastRegion(t0) {
  const $ = _c(14);
  let props;
  let state;
  let t1;
  if ($[0] !== t0) {
    ({
      state,
      position: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = state;
    $[3] = t1;
  } else {
    props = $[1];
    state = $[2];
    t1 = $[3];
  }
  const position = t1 === undefined ? "bottom" : t1;
  const ref = React.useRef(null);
  const {
    regionProps
  } = useToastRegion(props, state, ref);
  const t2 = position === "bottom";
  const t3 = position === "middle";
  const t4 = position === "top";
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = clsx(styles.toastRegion, {
      [styles.toastRegionBottom]: t2,
      [styles.toastRegionMiddle]: t3,
      [styles.toastRegionTop]: t4
    });
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== state) {
    t6 = state.visibleToasts.map(toast => <Toast key={toast.key} toast={toast} state={state} />);
    $[8] = state;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== regionProps || $[11] !== t5 || $[12] !== t6) {
    t7 = <div {...regionProps} ref={ref} className={t5}>{t6}</div>;
    $[10] = regionProps;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
export function ToastProvider(t0) {
  const $ = _c(32);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const [queue] = useState(_temp);
  const state = useToastQueue(queue);
  let t1;
  if ($[3] !== queue) {
    t1 = (content, options) => {
      for (const toast of queue.visibleToasts) {
        queue.close(toast.key);
      }
      return queue.add(content, options);
    };
    $[3] = queue;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== state || $[6] !== t1) {
    t2 = {
      ...state,
      add: t1
    };
    $[5] = state;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const replacingState = t2;
  let t3;
  if ($[8] !== state.visibleToasts) {
    t3 = state.visibleToasts.filter(_temp2);
    $[8] = state.visibleToasts;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const bottomToasts = t3;
  let t4;
  if ($[10] !== state.visibleToasts) {
    t4 = state.visibleToasts.filter(_temp3);
    $[10] = state.visibleToasts;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const middleToasts = t4;
  let t5;
  if ($[12] !== state.visibleToasts) {
    t5 = state.visibleToasts.filter(_temp4);
    $[12] = state.visibleToasts;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const topToasts = t5;
  const portalTarget = typeof document !== "undefined" ? document.body : null;
  let t6;
  if ($[14] !== props || $[15] !== state || $[16] !== topToasts) {
    t6 = portalTarget && topToasts.length > 0 && createPortal(<ToastRegion {...props} state={{
      ...state,
      visibleToasts: topToasts
    }} position="top" />, portalTarget);
    $[14] = props;
    $[15] = state;
    $[16] = topToasts;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== middleToasts || $[19] !== props || $[20] !== state) {
    t7 = portalTarget && middleToasts.length > 0 && createPortal(<ToastRegion {...props} state={{
      ...state,
      visibleToasts: middleToasts
    }} position="middle" />, portalTarget);
    $[18] = middleToasts;
    $[19] = props;
    $[20] = state;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== bottomToasts || $[23] !== props || $[24] !== state) {
    t8 = portalTarget && bottomToasts.length > 0 && createPortal(<ToastRegion {...props} state={{
      ...state,
      visibleToasts: bottomToasts
    }} position="bottom" />, portalTarget);
    $[22] = bottomToasts;
    $[23] = props;
    $[24] = state;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== children || $[27] !== replacingState || $[28] !== t6 || $[29] !== t7 || $[30] !== t8) {
    t9 = <ToastContext.Provider value={replacingState}>{children}{t6}{t7}{t8}</ToastContext.Provider>;
    $[26] = children;
    $[27] = replacingState;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
}
function _temp4(toast_2) {
  return toast_2.content.position === "top";
}
function _temp3(toast_1) {
  return toast_1.content.position === "middle";
}
function _temp2(toast_0) {
  return !toast_0.content.position || toast_0.content.position === "bottom";
}
function _temp() {
  return new ToastQueue({
    maxVisibleToasts: 5,
    hasExitAnimation: true
  });
}
export function useToasts() {
  const toastsState = React.useContext(ToastContext);
  if (!toastsState) {
    throw new Error("useToasts must be used within a ToastProvider");
  }
  return toastsState;
}